import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { push } from '@r1-frontend/analytics/campaignLayer';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { InfoCircleFlip } from '@r1-frontend/ui-react/components/svg/main';
import { H4 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import TextWrapper from '@r1-frontend/ui-react/components/typography/TextWrapper';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { FullWidthListContainer, ListContainer, NonStaticContainer } from '@r1-frontend/ui-react/experimental/containers';
import { AdvertisingLabelWithTooltip } from '@r1-frontend/ui-react/organisms/AdvertisingLabelWithTooltip';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { specialOfferAnalytics } from '~/src/features/specialOffersContainer/analytics';
import LabelsList from '~/src/features/specialOffersContainer/components/LabelsList';
import { DefaultCallback } from '~/src/features/specialOffersContainer/constants';
import { useConnectOffer } from '~/src/features/specialOffersContainer/hooks';
import { setPopupState, setSelectedOfferId } from '~/src/features/specialOffersContainer/store/actions';
import { viewOffer } from '~/src/features/specialOffersContainer/store/actions/thunk';
import { IOfferWithExtendedAdvertising, TPromoCallbackMap } from '~/src/features/specialOffersContainer/types';
import { useElementVisibility } from '~/src/helpers/hooks/useElementVisibility';

import * as ST from './styled';

interface IProps {
    offer: IOfferWithExtendedAdvertising,
    callbackMap: TPromoCallbackMap<typeof DefaultCallback>,
}

const LkPromotionalCard = ({ offer, callbackMap }: IProps): JSX.Element => {
    const {
        id,
        advertising,
        benefits,
        billingState,
        colorLabels,
        hasInstantConnect,
        imageV2,
        navigation,
        paySum,
        requestId,
        shortDescription,
        title,

        isConnecting,
        isLoading,
    } = offer;

    const dispatch = useDispatch();

    const { isLocalLoading, activateButtonText, connectOffer } = useConnectOffer(callbackMap, offer);

    const onInfoClick = () => {
        dispatch(viewOffer(offer));
    };

    const onConnectHandler = () => {
        if (!hasInstantConnect) {
            onInfoClick();
            return;
        }

        dispatch(setSelectedOfferId(requestId));

        if (!navigation?.type) {
            specialOfferAnalytics.instantConnect(offer);

            switch (true) {
                case !!paySum:
                    dispatch(setPopupState('needPayment'));
                    return;
                case billingState !== 'connected' && (benefits?.some(({ needPhone }) => needPhone) ?? false):
                    dispatch(setPopupState('needSetPhone'));
                    return;
                default:
                    dispatch(setPopupState('needConfirmation'));
                    return;
            }
        }

        connectOffer();
    };

    const labels = useMemo(() => {
        if (!colorLabels) {
            return;
        }

        return colorLabels.slice(0, 2);
    }, [colorLabels]);

    const pushCampaignSpecOfferView = useCallback(() => {
        push({ id, type: 'spec-offer', actionType: 'view' });
    }, [id]);

    const specOfferRef = useElementVisibility(pushCampaignSpecOfferView);

    return (
        <ST.CardWrapper ref={specOfferRef}>
            <ST.CardImage>
                <NonStaticContainer
                    $united={{
                        $position: 'absolute',
                        $top: 24,
                        $left: 24,
                        $right: 24,
                    }}
                >
                    <FullWidthListContainer $direction='row' $wrap='nowrap' $justifyContent={'space-between'} $size={0}>
                        {labels && (
                            <LabelsList labels={labels}/>
                        )}
                        {advertising && (
                            <AdvertisingLabelWithTooltip
                                onClick={advertising.onClick}
                                companyName={advertising.companyName}
                                erid={advertising.erid}
                                tooltip={{
                                    position: 'bottom-right',
                                    isShown: advertising.isShown,
                                    onClose: advertising.onClose,
                                }}
                            />
                        )}
                    </FullWidthListContainer>
                </NonStaticContainer>
                <WebpImage urlWebp={imageV2?.urlWebp}>
                    <img src={imageV2?.url} alt={title}/>
                </WebpImage>
            </ST.CardImage>
            <ST.ContentWrapper $united={[16]}>
                <ST.CardContent $direction={'column'} $wrap={'nowrap'} $justifyContent={'space-between'} $desktop={16} $mobile={8}>
                    <TextWrapper $lastIndent={false}>
                        <H4 $color={'TextPrimary'} $withIndent={false} $desktop>{title}</H4>
                        <Paragraph4
                            dangerouslySetInnerHTML={{ __html: shortDescription }}
                            $color={COLORS.TextPrimary}
                            $smaller
                        />
                    </TextWrapper>
                    <ListContainer $direction={'row'} $wrap={'nowrap'} $size={8}>
                        <Button
                            size={'large'}
                            bType={'secondary'}
                            loading={isConnecting || isLocalLoading}
                            disabled={isLoading}
                            onClick={onConnectHandler}
                        >
                            {hasInstantConnect ? activateButtonText : 'Подробнее'}
                        </Button>
                        {!!hasInstantConnect && (
                            <Button
                                size={'large'}
                                bType={'secondary'}
                                Icon={<InfoCircleFlip size={24}/>}
                                disabled={isLoading}
                                rounded
                                onClick={onInfoClick}
                            />
                        )}
                    </ListContainer>
                </ST.CardContent>
            </ST.ContentWrapper>
        </ST.CardWrapper>
    );
};

export default LkPromotionalCard;
